import { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from 'store/hook';
import TireInventoryHandler from 'handlers/tire-inventory.handler';
import { type CreateTireInventoryRequest } from 'types/tire-inventory.types';
import { type Asset } from 'types/asset.types';
import { ThemePalette } from 'mui.theme';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getDynamicAssetTireInfo } from 'views/TirePressure/util';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertDisplayTimeToUtc } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  title: string;
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
  onSuccess?: () => Promise<void>;
}

export const CreateTireInventoryModal = ({
  title,
  open,
  handleAbort,
  enableDarkTheme,
  onSuccess,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const assets = useAppSelector((state) => state.assetReducer.assets);
  const tirePatternPositions = useAppSelector(
    (state) => state.tirePatternReducer.tirePatternPositions
  );
  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );

  const tireInventoryHandler = new TireInventoryHandler();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  // Get current date/time in customer's timezone for installation date default
  const getCurrentDateTime = () => {
    const now = dayjs().tz(timezoneBasedOnDisplayMode);
    return now.format('YYYY-MM-DDTHH:mm');
  };

  const [formData, setFormData] = useState<CreateTireInventoryRequest>({
    tireSerialNumber: '',
    tireManufacturer: '',
    tireModel: '',
    tireSize: '',
    initialTreadDepth: undefined,
    maxLoadIndex: undefined,
    tireMfdDate: '',
    tireExpiryDate: '',
    currentStatus: 'IN_STORAGE',
    assetId: '',
    position: '',
    installationDate: getCurrentDateTime(),
    odometerAtInstall: undefined,
    engineHoursAtInstall: undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  // Get tire positions for selected asset
  const availableTirePositions = useMemo(() => {
    if (
      !selectedAsset ||
      !tirePatternPositions.length ||
      !makeModelDetails.length
    ) {
      return [];
    }

    const { tireCodes } = getDynamicAssetTireInfo(
      makeModelDetails,
      tirePatternPositions,
      selectedAsset.make,
      selectedAsset.model,
      customerId
    );

    return Object.entries(tireCodes).map(([code, label]) => ({
      code,
      label,
    }));
  }, [selectedAsset, tirePatternPositions, makeModelDetails, customerId]);

  // Reset asset and tire position when status changes
  useEffect(() => {
    if (formData.currentStatus !== 'IN_SERVICE') {
      setFormData((prev) => ({
        ...prev,
        assetId: '',
        position: '',
        installationDate: getCurrentDateTime(),
        odometerAtInstall: undefined,
        engineHoursAtInstall: undefined,
      }));
      setSelectedAsset(null);
    } else {
      // When switching TO IN_SERVICE, set installation date to current if empty
      if (!formData.installationDate) {
        setFormData((prev) => ({
          ...prev,
          installationDate: getCurrentDateTime(),
        }));
      }
    }
  }, [formData.currentStatus]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.tireSerialNumber) {
      newErrors.tireSerialNumber = 'Tire serial number is required';
    } else if (formData.tireSerialNumber.length > 255) {
      newErrors.tireSerialNumber = 'Maximum 255 characters';
    }

    if (!formData.currentStatus) {
      newErrors.currentStatus = 'Status is required';
    }

    // Validate IN_SERVICE requirements
    if (formData.currentStatus === 'IN_SERVICE') {
      if (!formData.assetId) {
        newErrors.assetId = 'Asset is required for IN_SERVICE status';
      }
      if (!formData.position) {
        newErrors.position = 'Tire position is required for IN_SERVICE status';
      }
      if (!formData.installationDate) {
        newErrors.installationDate =
          'Installation date is required for IN_SERVICE status';
      }
      if (
        formData.odometerAtInstall === undefined ||
        formData.odometerAtInstall === null
      ) {
        newErrors.odometerAtInstall =
          'Odometer reading is required for IN_SERVICE status';
      } else if (formData.odometerAtInstall < 0) {
        newErrors.odometerAtInstall = 'Must be a positive number';
      }
      if (
        formData.engineHoursAtInstall === undefined ||
        formData.engineHoursAtInstall === null
      ) {
        newErrors.engineHoursAtInstall =
          'Engine hours is required for IN_SERVICE status';
      } else if (formData.engineHoursAtInstall < 0) {
        newErrors.engineHoursAtInstall = 'Must be a positive number';
      }
    }

    if (formData.tireManufacturer && formData.tireManufacturer.length > 255) {
      newErrors.tireManufacturer = 'Maximum 255 characters';
    }
    if (formData.tireModel && formData.tireModel.length > 255) {
      newErrors.tireModel = 'Maximum 255 characters';
    }
    if (formData.tireSize && formData.tireSize.length > 255) {
      newErrors.tireSize = 'Maximum 255 characters';
    }

    if (
      formData.initialTreadDepth !== undefined &&
      (formData.initialTreadDepth < 0 || formData.initialTreadDepth > 100)
    ) {
      newErrors.initialTreadDepth = 'Must be between 0 and 100 mm';
    }

    if (formData.maxLoadIndex !== undefined && formData.maxLoadIndex < 0) {
      newErrors.maxLoadIndex = 'Must be a positive number';
    }

    if (formData.tireMfdDate) {
      const mfdDate = dayjs(formData.tireMfdDate);
      if (mfdDate.isAfter(dayjs())) {
        newErrors.tireMfdDate = 'Manufacturing date cannot be in the future';
      }
    }

    if (formData.tireExpiryDate) {
      const expDate = dayjs(formData.tireExpiryDate);
      if (expDate.isBefore(dayjs())) {
        newErrors.tireExpiryDate = 'Expiry date must be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    field: keyof CreateTireInventoryRequest,
    value: any
  ) => {
    setFormData({
      ...formData,
      [field]: value === '' ? undefined : value,
    });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleAssetChange = (assetId: string) => {
    const asset = assets.find((a) => a.assetId === assetId);
    setSelectedAsset(asset ?? null);
    setFormData({
      ...formData,
      assetId,
      position: '', // Reset tire position when asset changes
    });
    if (errors.assetId) {
      setErrors({ ...errors, assetId: '', position: '' });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Clean up the data - convert empty strings to undefined to avoid validation errors
    const cleanedData: CreateTireInventoryRequest = {
      tireSerialNumber: formData.tireSerialNumber,
      tireManufacturer: formData.tireManufacturer
        ? formData.tireManufacturer
        : undefined,
      tireModel: formData.tireModel ? formData.tireModel : undefined,
      tireSize: formData.tireSize ? formData.tireSize : undefined,
      initialTreadDepth: formData.initialTreadDepth,
      maxLoadIndex: formData.maxLoadIndex,
      tireMfdDate: formData.tireMfdDate ? formData.tireMfdDate : undefined,
      tireExpiryDate: formData.tireExpiryDate
        ? formData.tireExpiryDate
        : undefined,
      currentStatus: formData.currentStatus,
      assetId:
        formData.currentStatus === 'IN_SERVICE' ? formData.assetId : undefined,
      position:
        formData.currentStatus === 'IN_SERVICE' ? formData.position : undefined,
      installationDate:
        formData.currentStatus === 'IN_SERVICE' && formData.installationDate
          ? convertDisplayTimeToUtc(
              formData.installationDate,
              'configured',
              timezoneBasedOnDisplayMode
            ).toISOString()
          : undefined,
      odometerAtInstall:
        formData.currentStatus === 'IN_SERVICE'
          ? formData.odometerAtInstall
          : undefined,
      engineHoursAtInstall:
        formData.currentStatus === 'IN_SERVICE'
          ? formData.engineHoursAtInstall
          : undefined,
    };

    const success = await tireInventoryHandler.createTireInventory(
      cleanedData,
      customerId
    );

    if (success) {
      // Wait for the inventory to refresh before closing the modal
      await onSuccess?.();
      handleAbort();
    }
  };

  const isFormValid =
    !!formData.tireSerialNumber &&
    !!formData.currentStatus &&
    (formData.currentStatus !== 'IN_SERVICE' ||
      (!!formData.assetId &&
        !!formData.position &&
        !!formData.installationDate &&
        formData.odometerAtInstall !== undefined &&
        formData.engineHoursAtInstall !== undefined));

  const statusConfig = {
    IN_SERVICE: { label: t('in_service'), color: 'success' as const },
    IN_STORAGE: { label: t('in_storage'), color: 'info' as const },
    AT_RETREAD: { label: t('at_retread'), color: 'warning' as const },
    AT_REPAIR: { label: t('at_repair'), color: 'warning' as const },
    SCRAPPED: { label: t('scrapped'), color: 'error' as const },
  };

  return (
    <Dialog
      open={open}
      onClose={handleAbort}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: enableDarkTheme
            ? palette.modalBackground
            : palette.boxBackground,
          backgroundImage: 'none',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          pt: 2.5,
          px: 3,
          color: palette.textPrimary,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, fontSize: '0.875rem' }}
          >
            {t('fill_in_the_tire_information_below')}
          </Typography>
        </Box>
        <IconButton
          onClick={handleAbort}
          sx={{
            color: palette.textPrimary,
            '&:hover': {
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        <Grid container spacing={2} mt={2}>
          {/* Tire Serial Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('tire_serial_number')}
              placeholder={t('e_g_tire_abc_123')}
              value={formData.tireSerialNumber ?? ''}
              onChange={(e) => {
                handleChange('tireSerialNumber', e.target.value);
              }}
              error={!!errors.tireSerialNumber}
              helperText={
                errors.tireSerialNumber ?? 'Unique identifier for the tire'
              }
              inputProps={{ maxLength: 255 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          {/* Current Status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.currentStatus}>
              <InputLabel
                sx={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {t('current_status')}
              </InputLabel>
              <Select
                value={formData.currentStatus}
                label={t('current_status')}
                onChange={(e) => {
                  handleChange('currentStatus', e.target.value);
                }}
                renderValue={(selected) => {
                  const config = statusConfig[selected];
                  return (
                    <Chip
                      label={config.label}
                      color={config.color}
                      size="small"
                      sx={{ height: 24, fontWeight: 500 }}
                    />
                  );
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1.5px',
                  },
                  color: palette.textPrimary,
                }}
              >
                {Object.entries(statusConfig).map(([value, config]) => (
                  <MenuItem key={value} value={value}>
                    <Chip
                      label={config.label}
                      color={config.color}
                      size="small"
                      sx={{ height: 24, fontWeight: 500 }}
                    />
                  </MenuItem>
                ))}
              </Select>
              {errors.currentStatus && (
                <FormHelperText>{errors.currentStatus}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Asset Selection - Only for IN_SERVICE */}
          {formData.currentStatus === 'IN_SERVICE' && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.assetId}>
                  <InputLabel
                    sx={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.white
                        : 'rgba(0, 0, 0, 0.6)',
                    }}
                  >
                    {t('select_asset')}
                  </InputLabel>
                  <Select
                    value={formData.assetId ?? ''}
                    label={t('select_asset')}
                    onChange={(e) => {
                      handleAssetChange(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1.5px',
                      },
                      color: palette.textPrimary,
                    }}
                  >
                    {assets.map((asset) => (
                      <MenuItem key={asset.assetId} value={asset.assetId}>
                        {asset.bumperNumber} - {asset.make} {asset.model}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.assetId && (
                    <FormHelperText>{errors.assetId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  required
                  error={!!errors.position}
                  disabled={
                    !formData.assetId || availableTirePositions.length === 0
                  }
                >
                  <InputLabel
                    sx={{
                      color: enableDarkTheme
                        ? ThemePalette.typography.white
                        : 'rgba(0, 0, 0, 0.6)',
                    }}
                  >
                    {t('tire_position')}
                  </InputLabel>
                  <Select
                    value={formData.position ?? ''}
                    label={t('tire_position')}
                    onChange={(e) => {
                      handleChange('position', e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1.5px',
                      },
                      color: palette.textPrimary,
                    }}
                  >
                    {availableTirePositions.length === 0 ? (
                      <MenuItem value="" disabled>
                        {formData.assetId
                          ? 'No tire positions available'
                          : 'Select an asset first'}
                      </MenuItem>
                    ) : (
                      availableTirePositions.map(
                        (position: { code: string; label: string }) => (
                          <MenuItem key={position.code} value={position.label}>
                            {position.label}
                          </MenuItem>
                        )
                      )
                    )}
                  </Select>
                  {errors.position && (
                    <FormHelperText>{errors.position}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Installation Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="datetime-local"
                  label={t('installation_date_2')}
                  value={formData.installationDate ?? ''}
                  onChange={(e) => {
                    handleChange('installationDate', e.target.value);
                  }}
                  error={!!errors.installationDate}
                  helperText={
                    errors.installationDate ?? 'When the tire was installed'
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderWidth: '1.5px',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Odometer at Install */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label={t('odometer_at_install')}
                  placeholder={t('e_g_50000')}
                  value={formData.odometerAtInstall ?? ''}
                  onChange={(e) => {
                    handleChange(
                      'odometerAtInstall',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    );
                  }}
                  error={!!errors.odometerAtInstall}
                  helperText={
                    errors.odometerAtInstall ??
                    'Odometer reading at installation'
                  }
                  inputProps={{ min: 0, step: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderWidth: '1.5px',
                      },
                    },
                  }}
                />
              </Grid>

              {/* Engine Hours at Install */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label={t('engine_hours_at_install')}
                  placeholder={t('e_g_2500')}
                  value={formData.engineHoursAtInstall ?? ''}
                  onChange={(e) => {
                    handleChange(
                      'engineHoursAtInstall',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    );
                  }}
                  error={!!errors.engineHoursAtInstall}
                  helperText={
                    errors.engineHoursAtInstall ??
                    'Engine hours at installation'
                  }
                  inputProps={{ min: 0, step: 0.1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderWidth: '1.5px',
                      },
                    },
                  }}
                />
              </Grid>
            </>
          )}

          {/* Tire Manufacturer */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('tire_manufacturer')}
              placeholder={t('e_g_michelin_bridgestone')}
              value={formData.tireManufacturer ?? ''}
              onChange={(e) => {
                handleChange('tireManufacturer', e.target.value);
              }}
              error={!!errors.tireManufacturer}
              helperText={errors.tireManufacturer}
              inputProps={{ maxLength: 255 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('tire_model')}
              placeholder={t('e_g_xdr_x_one_line_energy_t')}
              value={formData.tireModel ?? ''}
              onChange={(e) => {
                handleChange('tireModel', e.target.value);
              }}
              error={!!errors.tireModel}
              helperText={errors.tireModel}
              inputProps={{ maxLength: 255 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('tire_size')}
              placeholder={t('e_g_295_75r22_5')}
              value={formData.tireSize ?? ''}
              onChange={(e) => {
                handleChange('tireSize', e.target.value);
              }}
              error={!!errors.tireSize}
              helperText={errors.tireSize}
              inputProps={{ maxLength: 255 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={t('max_load_index')}
              placeholder={t('e_g_146_148_150')}
              value={formData.maxLoadIndex ?? ''}
              onChange={(e) => {
                handleChange(
                  'maxLoadIndex',
                  e.target.value ? parseInt(e.target.value) : undefined
                );
              }}
              error={!!errors.maxLoadIndex}
              helperText={
                errors.maxLoadIndex || 'Tire load carrying capacity rating'
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          {/* Initial Tread Depth */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={t('initial_tread_depth_mm')}
              placeholder={t('enter_tread_depth')}
              value={formData.initialTreadDepth ?? ''}
              onChange={(e) => {
                handleChange(
                  'initialTreadDepth',
                  e.target.value ? parseFloat(e.target.value) : undefined
                );
              }}
              error={!!errors.initialTreadDepth}
              helperText={
                errors.initialTreadDepth ||
                'Original tread depth when new (0-100mm)'
              }
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={t('manufacturing_date')}
              value={formData.tireMfdDate ?? ''}
              onChange={(e) => {
                handleChange('tireMfdDate', e.target.value);
              }}
              error={!!errors.tireMfdDate}
              helperText={errors.tireMfdDate || 'Date tire was manufactured'}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={t('expiry_date')}
              value={formData.tireExpiryDate ?? ''}
              onChange={(e) => {
                handleChange('tireExpiryDate', e.target.value);
              }}
              error={!!errors.tireExpiryDate}
              helperText={errors.tireExpiryDate ?? 'Future expiration date'}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <Button
          onClick={handleAbort}
          variant="outlined"
          size="large"
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.primary,
            borderColor: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.primary,
            borderWidth: '1.5px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          disabled={!isFormValid}
          sx={{
            backgroundColor: ThemePalette.typography.primary,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            '&:hover': {
              backgroundColor: ThemePalette.typography.primary,
              opacity: 0.9,
              boxShadow: 3,
            },
            '&:disabled': {
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          {t('create_tire')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
